import { useState, type FormEvent, type ReactNode } from "react";
import { Reveal } from "./Reveal";
import { Mail, Phone, MapPin, ExternalLink, Send } from "lucide-react";
import { notify } from "@/lib/notify";

const fieldClass =
  "flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
const textareaClass =
  "flex min-h-[60px] w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

const items = [
  {
    icon: Mail,
    label: "Email (fastest)",
    value: "saca@ix.netcom.com",
    href: "mailto:saca@ix.netcom.com",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "(864) 616-2403",
    href: "tel:+18646162403",
    note: "Voice mail can be unreliable — email is faster.",
  },
  {
    icon: MapPin,
    label: "Visit",
    value: "Needful Things Mall · Hendersonville, NC",
  },
  {
    icon: ExternalLink,
    label: "eBay store",
    value: "carolinamountainstamps",
    href: "https://www.ebay.com/str/carolinamountainstamps",
  },
];

export function Contact() {
  return (
    <section id="contact" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <Reveal>
              <p className="mb-4 text-xs uppercase tracking-[0.28em] text-accent">
                Get in touch
              </p>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl leading-tight text-primary md:text-5xl">
                Ask about a country. A topic. A single stamp.
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 text-muted-foreground">
                We answer email quickly and personally. Tell us what you
                collect and we'll tell you what we have.
              </p>
            </Reveal>

            <div className="mt-8 border border-border bg-card p-6 shadow-[var(--shadow-stamp)]">
              <ul className="divide-y divide-border">
              {items.map((it) => (
                  <li key={it.label} className="flex items-start gap-4 py-4 first:pt-0 last:pb-0">
                    <span className="mt-0.5 grid h-9 w-9 place-items-center rounded-sm bg-primary/10 text-primary">
                      <it.icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        {it.label}
                      </div>
                      {it.href ? (
                        <a
                          href={it.href}
                          target={it.href.startsWith("http") ? "_blank" : undefined}
                          rel={it.href.startsWith("http") ? "noreferrer noopener" : undefined}
                            className="mt-1 block text-lg text-primary hover:text-accent"
                        >
                          {it.value}
                          {it.href.startsWith("http") && (
                            <span className="sr-only"> (opens in a new tab)</span>
                          )}
                        </a>
                      ) : (
                        <div className="mt-1 text-lg text-primary">{it.value}</div>
                      )}
                      {it.note && (
                        <p className="mt-1 text-xs text-muted-foreground">{it.note}</p>
                      )}
                    </div>
                  </li>
              ))}
              </ul>
            </div>
          </div>

          <Reveal>
            <ContactForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ContactFields = {
  name: string;
  email: string;
  subject: string;
  message: string;
};
type FieldErrors = Partial<Record<keyof ContactFields, string>>;

function validateContact(values: ContactFields): FieldErrors {
  const errors: FieldErrors = {};
  const name = values.name.trim();
  const email = values.email.trim();
  const subject = values.subject.trim();
  const message = values.message.trim();

  if (!name) errors.name = "Please enter your name";
  else if (name.length > 80) errors.name = "Please enter your name";

  if (!email || !EMAIL_RE.test(email) || email.length > 200) {
    errors.email = "Please enter a valid email";
  }

  if (subject.length < 2) errors.subject = "Please add a subject";
  else if (subject.length > 120) errors.subject = "Please add a subject";

  if (message.length < 10) errors.message = "Tell us a little more (10+ chars)";
  else if (message.length > 2000) errors.message = "Tell us a little more (10+ chars)";

  return errors;
}

function ContactForm() {
  const [values, setValues] = useState({
    name: "",
    email: "",
    subject: "Stamp inquiry",
    message: "",
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof typeof values>(k: K, v: string) => {
    setValues((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const fieldErrors = validateContact(values);
    if (Object.keys(fieldErrors).length) {
      setErrors(fieldErrors);
      void notify("error", "Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    fetch("/api/public/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: values.name.trim(),
        email: values.email.trim(),
        subject: values.subject.trim(),
        message: values.message.trim(),
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          throw new Error(data?.error || "Failed to send");
        }
        void notify("success", "Message sent!", "Thanks — we'll reply to your email soon.");
        setValues({
          name: "",
          email: "",
          subject: "Stamp inquiry",
          message: "",
        });
      })
      .catch(() => {
        void notify(
          "error",
          "Couldn't send your message",
          "Please try again or email saca@ix.netcom.com directly.",
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="relative border border-border bg-card p-6 md:p-8 shadow-[var(--shadow-elegant)]"
    >
      <div className="pointer-events-none absolute inset-2 border border-dashed border-border/70" />

      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.28em] text-accent">
          Send a message
        </p>
        <h3 className="mt-2 font-display text-2xl text-primary">
          Write to Carolina Mountain Stamps
        </h3>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <FieldWrapper label="Name" htmlFor="cf-name" error={errors.name}>
            <input
              id="cf-name"
              className={fieldClass}
              autoComplete="name"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Your name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "cf-name-error" : undefined}
              required
              maxLength={80}
            />
          </FieldWrapper>
          <FieldWrapper label="Email" htmlFor="cf-email" error={errors.email}>
            <input
              id="cf-email"
              className={fieldClass}
              type="email"
              autoComplete="email"
              value={values.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="you@example.com"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "cf-email-error" : undefined}
              required
              maxLength={200}
            />
          </FieldWrapper>
        </div>

        <div className="mt-4">
          <FieldWrapper label="Subject" htmlFor="cf-subject" error={errors.subject}>
            <input
              id="cf-subject"
              className={fieldClass}
              value={values.subject}
              onChange={(e) => set("subject", e.target.value)}
              placeholder="Looking for pre-1900 U.S. issues…"
              aria-invalid={!!errors.subject}
              aria-describedby={errors.subject ? "cf-subject-error" : undefined}
              required
              maxLength={120}
            />
          </FieldWrapper>
        </div>

        <div className="mt-4">
          <FieldWrapper label="Message" htmlFor="cf-message" error={errors.message}>
            <textarea
              id="cf-message"
              className={textareaClass}
              value={values.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Countries, topics, want list, or a specific stamp…"
              rows={6}
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? "cf-message-error" : undefined}
              required
              maxLength={2000}
            />
          </FieldWrapper>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Sent straight to our inbox — no account needed.
          </p>
          <button
            type="submit"
            disabled={submitting}
            className="group inline-flex min-h-12 items-center gap-2 rounded-sm bg-accent px-5 py-3 text-sm font-medium text-accent-foreground shadow-[var(--shadow-stamp)] transition-transform hover:-translate-y-0.5 disabled:opacity-70"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            {submitting ? "Sending…" : "Send message"}
          </button>
        </div>
      </div>
    </form>
  );
}

function FieldWrapper({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground"
      >
        {label}
      </label>
      <div className="mt-2">{children}</div>
      {error && (
        <p id={`${htmlFor}-error`} role="alert" className="mt-1.5 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}